import React, { useState } from 'react';
import { Timestamp } from 'firebase/firestore';
import { useOpenProcesses } from '../../../../hooks/useOpenProcesses';
import { DashboardNotification } from '@sp-types/notificationType';
import { ActionSection, ColorBorder, NotificationBody, NotificationIcon } from './components/NotificationParts';
import { GENERAL_ID } from '../../../../components/Stores/exposureDataStore';
import { useNavigate } from 'react-router-dom';
import { PopoverConfirmOverlay } from '../../../../components/UI-Elements/PopoverOverlays/PopoverConfirmOverlay';
import { updateFileBatch } from '../../../../sp-firebase/fileBatchesFB';
import { updateFutureOrder } from '../../../../sp-firebase/futureOrderFB';
import { updateBalanceFlow } from '../../../../sp-firebase/balanceFlowFB';
import { updateProduct } from '../../../../sp-firebase/productFB';
import { updateInventoryReport } from '../../../../sp-firebase/inventoryReportFB';
import { updateNotification } from '../../../../sp-firebase/notificationsFB';
import { ToggleChips } from '../ToggleChips';

interface Props {
  notifications: DashboardNotification[];
  currentMaterialId: string;
  accountId?: string;
  colorsMap: Record<string, string>;
  isLoading: boolean;
  colorsToMaterial: Record<string, string>;
  refetchNotifications: () => void;
  editEnabled: boolean;
  setProportion?: (_showProportional: boolean) => void;
}

export const NotificationDashboardCard: React.FC<Props> = ({
  notifications,
  accountId,
  colorsMap,
  currentMaterialId,
  refetchNotifications,
  editEnabled,
  setProportion,
}) => {
  const [isNotificationsExpanded, setIsNotificationsExpanded] = useState(true);
  const { data: inProcessData, refetch: refetchProcesses } = useOpenProcesses(accountId);
  const [isInProcessExpanded, setIsInProcessExpanded] = useState(!!inProcessData);

  React.useEffect(() => {
    if (inProcessData) {
      setIsInProcessExpanded(inProcessData?.length > 0);
    }
  }, [inProcessData]);

  const [deleteNotifId, setDeleteNotifId] = useState(-1);

  const deleteNotification = async (notifIndex: number) => {
    setIsDeleting(true);

    const notifItem = filterNotificationByMaterial(notifications, currentMaterialId)?.[notifIndex];
    await updateNotification(notifItem.id, { dismissAt: Timestamp.now() });

    setDeleteNotifId(-1);

    await refetchNotifications();
    setIsDeleting(false);
  };
  const [isDeleting, setIsDeleting] = useState(false);

  const dismissInProcess = async (processIndex: number) => {
    setIsDeleting(true);
    const inProcItem = filterNotificationByMaterial(inProcessData || [], currentMaterialId)?.[processIndex];
    switch (inProcItem?.entity) {
      case 'fileBatch':
        await updateFileBatch(accountId || '', inProcItem.id, { dismissAt: Timestamp.now() });
        break;
      case 'futureOrder':
        await updateFutureOrder(inProcItem.id, { dismissAt: Timestamp.now() });
        break;
      case 'balanceFlow':
        await updateBalanceFlow(inProcItem.id, { dismissAt: Timestamp.now() });
        break;
      case 'product':
        await updateProduct(inProcItem.id, { dismissAt: Timestamp.now() });
        break;
      case 'inventoryReport':
        await updateInventoryReport(inProcItem.id, { dismissAt: Timestamp.now() });
        break;
    }

    await refetchProcesses();
    setIsDeleting(false);
  };
  const [isProp, setIsProp] = React.useState<number>(0);
  return (
    <div className="flex flex-col h-full bg-white shadow-md ">
      <div className="flex flex-col h-full overflow-hidden w-full">
        {notifications.length || inProcessData?.length ? (
          <>
            <div className={`flex flex-col ${isInProcessExpanded ? 'h-1/2' : ''} min-h-0`}>
              <FoldableNotificationHeader
                text="In Process"
                isExpended={isInProcessExpanded}
                onClick={() => setIsInProcessExpanded(!isInProcessExpanded)}
              />
              {isInProcessExpanded && inProcessData && (
                <div className="flex-1 overflow-auto">
                  <NotificationData
                    colorsMap={colorsMap}
                    onDelete={editEnabled ? dismissInProcess : undefined}
                    notifications={filterNotificationByMaterial(inProcessData, currentMaterialId)}
                    editEnabled={editEnabled}
                    isLoading={isDeleting}
                  />
                </div>
              )}
            </div>

            <div className={`flex flex-col ${isInProcessExpanded ? 'h-1/2' : 'flex-1'} min-h-0`}>
              <FoldableNotificationHeader
                text="Notifications"
                isExpended={isNotificationsExpanded}
                onClick={() => setIsNotificationsExpanded(!isNotificationsExpanded)}
              />
              {isNotificationsExpanded && (
                <div className="flex-1 overflow-auto">
                  <NotificationData
                    colorsMap={colorsMap}
                    notifications={filterNotificationByMaterial(notifications, currentMaterialId)}
                    onDelete={editEnabled ? (index: number) => setDeleteNotifId(index) : undefined}
                    editEnabled={editEnabled}
                    isLoading={isDeleting}
                  />
                </div>
              )}
            </div>
            {setProportion && (
              <div className="flex justify-center p-2">
                <ToggleChips
                  chips={[
                    { key: 'Proportional', value: 1 },
                    { key: 'None', value: 0 },
                  ]}
                  selected={isProp}
                  setSelected={(i) => {
                    setIsProp(i);
                    setProportion(i === 1);
                  }}
                  width="5rem"
                />
              </div>
            )}
          </>
        ) : null}
        {notifications.length + (inProcessData?.length || 0) === 0 && (
          <div className="flex flex-col flex-1  content-center   ">
            <div className=" basis-1/3" />
            <div className=" basis-2/3">
              <img
                className="mx-auto aspect-ratio-auto  w-[7.5rem]"
                title="alert"
                src="/assets/pages/dashboard/trend-bar/no-notifications.svg"
                alt="no notifications"
              />
            </div>
          </div>
        )}
      </div>
      {deleteNotifId > -1 && (
        <PopoverConfirmOverlay
          text="Are you sure you want to delete this notification?"
          title="Confirm delete"
          handleCancel={() => setDeleteNotifId(-1)}
          handleConfirm={() => deleteNotification(deleteNotifId)}
          isDialogOpen
          showCancel
        />
      )}
    </div>
  );
};

interface INotificationHeaderProps {
  onClick: () => void;
  isExpended: boolean;
  text: string;
}

const FoldableNotificationHeader: React.FC<INotificationHeaderProps> = ({ isExpended, onClick, text }) => {
  return (
    <div
      onClick={onClick}
      className="flex flex-row justify-between  cursor-pointer shadow-custom border border-gray-125 bg-gray-75  text-blue-600 text-sm font-bold py-1 px-2"
    >
      <p className="">{text}</p>
      <img
        className={`${isExpended ? '-rotate-90' : 'rotate-90'} w-5 h-5`}
        src="./assets/icons/flat/chevron-right_purple.svg"
      ></img>
    </div>
  );
};

interface INotificationDataSectionProps {
  notifications: DashboardNotification[];
  colorsMap: Record<string, string>;
  onDelete?: (_index: number, entity: string) => void;
  editEnabled: boolean;
  isLoading?: boolean;
}

const NotificationData: React.FC<INotificationDataSectionProps> = ({
  colorsMap,
  notifications,
  onDelete,
  editEnabled = true,
  isLoading = false,
}): React.ReactNode => {
  const [expandedCell, setExpandedCell] = useState<number>();
  const [hoverCell, setHoverCell] = useState<number>(-1);
  const navigate = useNavigate();
  const mouseEnter = (index: number) => setHoverCell(index);
  const mouseLeave = (index: number) => (hoverCell === index ? setHoverCell(-1) : null);

  return notifications.map(
    ({ materialId, type, text, descriptionText, actionText, date, actionUrl, entity }, index) => {
      const notificationColor = (materialId?.length === 1 && colorsMap[materialId[0]]) || undefined;
      return (
        <div
          className="flex items-start notification border-b border-b-gray-200 group relative"
          key={index}
          onMouseEnter={() => mouseEnter(index)}
          onMouseLeave={() => mouseLeave(index)}
        >
          <ColorBorder color={notificationColor || '#5467B2'} />
          <div className="flex p-2 w-full">
            <NotificationIcon
              type={type}
              onDelete={onDelete ? () => onDelete(index, entity) : undefined}
              hovered={hoverCell === index}
              isLoading={isLoading}
            />
            <div
              className="flex flex-col gap-1 w-full"
              onClick={() => setExpandedCell(expandedCell === index ? -1 : index)}
            >
              <NotificationBody
                text={text}
                expanded={expandedCell === index}
                setExpanded={(isExpanded: boolean) => setExpandedCell(isExpanded ? index : -1)}
                desc={descriptionText}
              />
              <p className="text-xs text-gray-400">{getDateString(date)}</p>
              {expandedCell === index && actionUrl && editEnabled && (
                <ActionSection text={actionText || ''} isLoading={false} onAction={() => navigate(actionUrl)} />
              )}
            </div>
          </div>
        </div>
      );
    },
  );
};

const getDateString = (date: Date | Timestamp) => {
  const today = new Date();

  const updatedDate = date instanceof Timestamp ? date.toDate() : date;

  const diffMillis = today.getTime() - updatedDate.getTime();
  const diffHours = Math.floor(diffMillis / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMillis / (1000 * 60 * 60 * 24));

  if (diffHours < 24) {
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  } else {
    return updatedDate.toLocaleDateString();
  }
};

const filterNotificationByMaterial = (processNotifications: DashboardNotification[], filterById: string) =>
  filterById === GENERAL_ID
    ? processNotifications.filter((item) => !item.dismissAt)
    : processNotifications.filter((item) => !item.dismissAt && item.materialId?.includes(filterById));
